// Standard Library Imports
import React, { useEffect, useState, useContext } from 'react';
//External library imports
import { toast } from 'react-toastify';
import { UilInfoCircle, UilImport } from '@iconscout/react-unicons';
import _ from 'lodash';
import moment from 'moment';
import { ArcherContainer, ArcherElement } from 'react-archer';
//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../hooks';
import { AdminService } from '../../../services/BiddingService/CommonServices/AdminService';
import { CustomerService } from '../../../services/BiddingService/CommonServices/CustomerService';
import economicsClasses from './Economics.module.css';
import DateRangePicker from '../../../components/Inputs/DateRangePicker/DateRangePicker';
import { DropdownComponent } from '../../../components/Inputs/Input';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import Table from '../../../components/Table/Table';
import { RootEconomicsService } from '../../../services/BiddingService/Root/EconomicsService';
import { dkCurrencyFormat } from '../../../utils/currencyHelper';
import { AuthContext } from '../../../context/AuthContext';
import { ROOT_USER_ROLES, ADMIN_USER_ROLES } from '../../../constants';
import { AdminEconomicsService } from '../../../services/BiddingService/Admin/EconomicsService';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import AreaEnergyRevenueIndex from './ModalComponent/AreaEnergyRevenueIndex';
import ConsumptionIndex from './ModalComponent/ConsumptionIndex';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import { DateParam, StringParam, useQueryParams } from 'use-query-params';

const DefaultDropdownOption = {
    label: 'All',
    value: 'all',
};

const viewOptions = [
    { label: 'Customer', value: 'customer' },
    { label: 'Market', value: 'market' },
];

const getDropdownOption = (data = []) => {
    const temp = [
        {
            label: 'All',
            value: 'all',
        },
    ];
    data.map((el) => {
        temp.push({ label: el.name, value: el._id });
    });
    return temp;
};

const Ledger = ({ cId, setSelectedIndex }) => {
    const [startLoader, stopLoader] = useLoader();

    const [startDate, setStartDate] = useQueryParamWithDefault('startDate', DateParam, moment().startOf('month').toDate(), cId);
    const [endDate, setEndDate] = useQueryParamWithDefault('endDate', DateParam, moment().endOf('month').toDate(), cId);

    const { state } = useContext(AuthContext);
    const userRole = _.get(state, ['user', 'userRole']);
    const adminId = _.get(state, ['user', 'adminId']);
    const isRootUser = ROOT_USER_ROLES.includes(userRole);
    const isAdminUser = ADMIN_USER_ROLES.includes(userRole);
    const [admins, setAdmins] = useState([DefaultDropdownOption]);
    const [selectedAdmin, setSelectedAdmin] = useQueryParamWithDefault('admin', StringParam, isAdminUser ? adminId : DefaultDropdownOption.value, cId);
    const [customers, setCustomers] = useState([DefaultDropdownOption]);
    const [selectedCustomer, setSelectedCustomer] = useQueryParamWithDefault('customer', StringParam, DefaultDropdownOption.value, cId);
    const [selectedView, setSelectedView] = useQueryParamWithDefault('view', StringParam, viewOptions[0].value, cId);
    const [ledgerData, setLedgerData] = useState({
        rawData: {},
        tableData: [],
        overAllTotalRevenue: {
            total: 0,
            sc: 0,
            partner: 0,
            customer: 0,
            enabler: 0,
            energy: 0,
            scEnergy: 0,
            partnerEnergy: 0,
            enablerEnergy: 0,
            customerEnergy: 0,
            customerEnergyBeforeDhCost: 0,
        },
    });

    const [showConsumptionModal, setShowConsumptionModal] = useState({ status: false, data: null });

    const [, setOverviewParams] = useQueryParams({
        eConomics_1_admin: StringParam,
        eConomics_1_customer: StringParam,
        eConomics_1_startDate: DateParam,
        eConomics_1_endDate: DateParam,
    });

    const [areaWiseRevenueModal, setAreaWiseRevenueModal] = useState({ status: false, data: null });

    useEffect(() => {
        if (isRootUser) getAdmins();
    }, []);

    useEffect(() => {
        if (selectedCustomer && selectedAdmin) getLedgerData();
    }, [selectedCustomer, selectedAdmin]);

    useEffect(() => {
        if (selectedAdmin && selectedAdmin !== 'all') getCustomers();
    }, [selectedAdmin]);

    const getAdmins = () => {
        AdminService.GetAdmins(startLoader, handleGetAdminSuccess, handleError, stopLoader);
    };

    const handleGetAdminSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if (!selectedAdmin) setSelectedAdmin(options[0].value);
        setAdmins(options);
    };

    const getCustomers = () => {
        const params = { adminId: selectedAdmin };
        CustomerService.GetCustomers(params, startLoader, handleGetCustomerSuccess, handleError, stopLoader);
    };

    const handleGetCustomerSuccess = ({ data }) => {
        const options = getDropdownOption(data.data);
        if ((!selectedCustomer || selectedCustomer !== 'all') && isRootUser) setSelectedCustomer(options[0].value);
        setCustomers(options);
    };

    const handleError = (err) => {
        toast.error(err?.response?.data?.message);
    };

    const getLedgerData = () => {
        const params = {
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD'),
            customerId: selectedCustomer,
            ...(isRootUser ? { adminId: selectedAdmin } : {}),
            view: selectedView,
        };

        let service;
        if (isRootUser) service = RootEconomicsService;
        if (isAdminUser) service = AdminEconomicsService;

        service.GetLedgerData(params, startLoader, handleLedgerDataSuccess, handleError, stopLoader);
    };

    const handleLedgerDataSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'customersRevenue']).map((item) => {
            item.formatedTotal = dkCurrencyFormat(item.totalRevenue);
            item.formatedSc = dkCurrencyFormat(item.scTotalRevenue);
            item.formatedPartner = dkCurrencyFormat(item.partnerTotalRevenue);
            item.formatedEnabler = dkCurrencyFormat(item.enablerTotalRevenue);
            item.formatedCustomer = dkCurrencyFormat(item.customerTotalRevenue);
            const showEnergyRev = item?.market?.includes('aFRR') || item?.market?.includes('mFRR') || selectedView === 'customer' ? true : false;

            item.formatedEnergy = showEnergyRev ? (
                <div className={economicsClasses.Center} style={{ gap: '0.2vw' }}>
                    {dkCurrencyFormat(item.totalEnergyRevenue)}
                    <CustomTooltip content={'View consumption'}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowConsumptionModal({
                                    status: true,
                                    data: {
                                        dateRange: { startDate: startDate, endDate: endDate },
                                        customerId: item.customerId,
                                        customer: item.name,
                                        market: item.market || null,
                                    },
                                });
                            }}
                        >
                            <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                        </div>
                    </CustomTooltip>
                </div>
            ) : (
                '--'
            );

            item.formatedScEnergy = showEnergyRev ? dkCurrencyFormat(item.scTotalEnergyRevenue) : '--';
            item.formatedPartnerEnergy = showEnergyRev ? dkCurrencyFormat(item.partnerTotalEnergyRevenue) : '--';
            item.formatedEnablerEnergy = showEnergyRev ? dkCurrencyFormat(item.enablerTotalEnergyRevenue) : '--';
            item.formatedCustomerEnergy = showEnergyRev ? dkCurrencyFormat(item.customerTotalEnergyRevenue) : '--';
            item.onDoubleClick = () => {
                setOverviewParams({
                    eConomics_1_admin: item.adminId,
                    eConomics_1_customer: item.customerId,
                    eConomics_1_startDate: startDate,
                    eConomics_1_endDate: endDate,
                });
                setSelectedIndex(1);
            };

            return item;
        });

        const overAllRevenue = _.get(data, ['data', 'overAllRevenue'], {});

        setLedgerData({
            tableData: processedData,
            overAllTotalRevenue: {
                total: dkCurrencyFormat(Math.round(overAllRevenue.total || 0)),
                sc: dkCurrencyFormat(Math.round(overAllRevenue.sc || 0)),
                partner: dkCurrencyFormat(Math.round(overAllRevenue.partner || 0)),
                customer: dkCurrencyFormat(Math.round(overAllRevenue.customer || 0)),
                enabler: dkCurrencyFormat(Math.round(overAllRevenue.enabler || 0)),
                energy: dkCurrencyFormat(Math.round(overAllRevenue.energy || 0)),
                scEnergy: dkCurrencyFormat(Math.round(overAllRevenue.scEnergy || 0)),
                partnerEnergy: dkCurrencyFormat(Math.round(overAllRevenue.partnerEnergy || 0)),
                customerEnergy: dkCurrencyFormat(Math.round(overAllRevenue.customerEnergy || 0)),
                enablerEnergy: dkCurrencyFormat(Math.round(overAllRevenue.enablerEnergy || 0)),
                customerEnergyBeforeDhCost: dkCurrencyFormat(Math.round(overAllRevenue.customerEnergyBeforeDhCost || 0)),
            },
            rawData: _.get(data, ['data'], {}),
        });
    };

    const exportData = () => {
        const excelData = _.get(ledgerData, ['rawData', 'customersRevenue'], []).map((item) => ({
            a: item.name,
            b: item.partner,
            c: item.scTotalRevenue,
            d: item.partnerTotalRevenue,
            e: item.enablerTotalRevenue,
            f: item.customerTotalRevenue,
            g: item.totalRevenue,

            h: item.scTotalEnergyRevenue,
            i: item.partnerTotalEnergyRevenue,
            j: item.enablerTotalEnergyRevenue,
            k: item.customerTotalEnergyRevenue,
            l: item.customerTotalEnergyBeforeDhCost,
            m: item.totalEnergyRevenue,
        }));
        excelData.push({
            a: 'Total',
            b: '',
            c: _.get(ledgerData, ['rawData', 'overAllRevenue', 'sc']),
            d: _.get(ledgerData, ['rawData', 'overAllRevenue', 'partner']),
            e: _.get(ledgerData, ['rawData', 'overAllRevenue', 'enabler']),
            f: _.get(ledgerData, ['rawData', 'overAllRevenue', 'customer']),
            g: _.get(ledgerData, ['rawData', 'overAllRevenue', 'total']),

            h: _.get(ledgerData, ['rawData', 'overAllRevenue', 'scEnergy']),
            i: _.get(ledgerData, ['rawData', 'overAllRevenue', 'partnerEnergy']),
            j: _.get(ledgerData, ['rawData', 'overAllRevenue', 'enablerEnergy']),
            k: _.get(ledgerData, ['rawData', 'overAllRevenue', 'customerEnergy']),
            l: _.get(ledgerData, ['rawData', 'overAllRevenue', 'customerEnergyBeforeDhCost']),
            m: _.get(ledgerData, ['rawData', 'overAllRevenue', 'energy']),
        });

        if (excelData.length > 1) {
            DownloadAsExcel(excelData, `Ledger-Export`, [
                'Customer',
                'Partner',
                'S.C. (CM) (€)',
                'Partner (CM) (€)',
                'Enabler (CM) (€)',
                'Customer (CM) (€)',
                'Total (CM) (€)',
                'S.C. (EAM) (€)',
                'Partner (EAM) (€)',
                'Enabler (EAM) (€)',
                'Customer (EAM) (€)',
                'Customer EAM (After Cost) (€)',
                'Total (EAM) (€)',
            ]);
        } else {
            toast.error('No data to export!');
        }
    };

    return (
        <div>
            <div className={economicsClasses.FilterBar}>
                <div className={economicsClasses.FilterContainer}>
                    {/* left container */}
                    <div className={economicsClasses.SubContainer}>
                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.DropDownContainer}>
                                    <label>Admin</label>
                                    <DropdownComponent
                                        name="adminId"
                                        options={admins}
                                        defaultValue={{ value: selectedAdmin }}
                                        onChange={(e) => {
                                            if (e.value !== selectedAdmin) setSelectedAdmin(e.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}

                        <div className={economicsClasses.DropDownContainer}>
                            <label>Customer</label>
                            <DropdownComponent
                                name="customerId"
                                options={customers}
                                defaultValue={{ value: selectedCustomer }}
                                onChange={(e) => {
                                    if (e.value !== selectedCustomer) {
                                        setSelectedCustomer(e.value);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* right container */}
                    <div className={economicsClasses.SubContainer}>
                        <div className={economicsClasses.DropDownContainer}>
                            <label>View</label>
                            <DropdownComponent
                                name="view"
                                options={viewOptions}
                                defaultValue={{ value: selectedView }}
                                onChange={(e) => {
                                    if (e.value !== selectedView) {
                                        setSelectedView(e.value);
                                    }
                                }}
                            />
                        </div>

                        {startDate && endDate && (
                            <div>
                                <DateRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(date) => {
                                        setStartDate(moment(date.startDate).toDate());
                                        setEndDate(moment(date.endDate).toDate());
                                    }}
                                    style={{ height: '2.5vw', marginBottom: '-0.9vh' }}
                                    showLabels={true}
                                />
                            </div>
                        )}

                        <div
                            style={{ marginTop: '0.8vh' }}
                            onClick={() => {
                                getLedgerData();
                                // setPage(0);
                            }}
                        >
                            <button className="btn-primary">Submit</button>
                        </div>

                        <div style={{ marginTop: '2vh' }} onClick={exportData}>
                            <UilImport
                                size="1.5vw"
                                style={{
                                    cursor: 'pointer',
                                    marginTop: '0.4vw',
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={economicsClasses.TableContainer}>
                <div className={economicsClasses.LedgerTreeContainer}>
                    <ArcherContainer
                        endShape={{
                            arrow: {
                                arrowLength: 0,
                                arrowThickness: 0,
                            },
                        }}
                    >
                        <div className={economicsClasses.TreeContainer}>
                            <div className={economicsClasses.OverAllWrapper}>
                                <div className={economicsClasses.Center}>
                                    <ArcherElement
                                        id="start"
                                        relations={[
                                            {
                                                targetId: 'center',
                                                targetAnchor: 'top',
                                                sourceAnchor: 'bottom',
                                                style: {
                                                    strokeColor: 'var(--color-primary)',
                                                    strokeWidth: 2,
                                                },
                                            },
                                        ]}
                                    >
                                        <div className={economicsClasses.RevenueCardTotal}>
                                            <p>Total (CM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'total'])} €</span>
                                        </div>
                                    </ArcherElement>
                                </div>

                                <ArcherElement
                                    id={'center'}
                                    relations={[
                                        {
                                            targetId: 'sc',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'partner',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'enabler',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'customer',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                    ]}
                                >
                                    <div className={economicsClasses.Center}></div>
                                </ArcherElement>
                                <div className={economicsClasses.OverRevContainer}>
                                    <ArcherElement id={'sc'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>S.C. (CM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'sc'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'partner'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Partner (CM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'partner'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'enabler'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Enabler (CM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'enabler'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'customer'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Customer (CM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'customer'])} €</span>
                                        </div>
                                    </ArcherElement>
                                </div>
                            </div>

                            <div className={economicsClasses.OverAllWrapper}>
                                <div className={economicsClasses.Center}>
                                    <ArcherElement
                                        id="start2"
                                        relations={[
                                            {
                                                targetId: 'centerEAM',
                                                targetAnchor: 'top',
                                                sourceAnchor: 'bottom',
                                                style: {
                                                    strokeColor: 'var(--color-primary)',
                                                    strokeWidth: 2,
                                                },
                                            },
                                        ]}
                                    >
                                        <div className={economicsClasses.RevenueCardTotal}>
                                            <p>Total (EAM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'energy'])} €</span>
                                        </div>
                                    </ArcherElement>
                                </div>

                                <ArcherElement
                                    id={'centerEAM'}
                                    relations={[
                                        {
                                            targetId: 'scEAM',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'partnerEAM',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'enablerEAM',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                        {
                                            targetId: 'customerEAM',
                                            targetAnchor: 'top',
                                            sourceAnchor: 'bottom',
                                            style: {
                                                strokeColor: 'var(--color-primary)',
                                                strokeWidth: 2,
                                            },
                                        },
                                    ]}
                                >
                                    <div className={economicsClasses.Center}></div>
                                </ArcherElement>
                                <div className={economicsClasses.OverRevContainer}>
                                    <ArcherElement id={'scEAM'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>S.C. (EAM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'scEnergy'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'partnerEAM'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Partner (EAM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'partnerEnergy'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'enablerEAM'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Enabler (EAM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'enablerEnergy'])} €</span>
                                        </div>
                                    </ArcherElement>
                                    <ArcherElement id={'customerEAM'}>
                                        <div className={economicsClasses.RevenueCard}>
                                            <p>Customer (EAM)</p>
                                            <span>{_.get(ledgerData, ['overAllTotalRevenue', 'customerEnergy'])} €</span>
                                        </div>
                                    </ArcherElement>
                                </div>
                            </div>
                        </div>
                    </ArcherContainer>
                </div>
                <Table
                    data={ledgerData.tableData}
                    head={[
                        'Customer',
                        'Partner',
                        'SC CM (€)',
                        'Partner CM (€)',
                        'Enablers CM (€)',
                        'Customer CM (€)',
                        'Total CM (€)',
                        'SC EAM (€)',
                        'Partner EAM (€)',
                        'Enablers EAM (€)',
                        'Customer EAM (€)',
                        <div
                            onClick={() => {
                                if (isRootUser) setAreaWiseRevenueModal({ status: true, data: { dateRange: { startDate: startDate, endDate: endDate } } });
                            }}
                        >
                            {isRootUser ? (
                                <CustomTooltip content={'Click to view area-wise energy revenue'}>
                                    <span>Total EAM (€)</span>
                                </CustomTooltip>
                            ) : (
                                <span>Total EAM (€)</span>
                            )}
                        </div>,
                    ]}
                    keys={[
                        'name',
                        'partner',
                        'formatedSc',
                        'formatedPartner',
                        'formatedEnabler',
                        'formatedCustomer',
                        'formatedTotal',
                        'formatedScEnergy',
                        'formatedPartnerEnergy',
                        'formatedEnablerEnergy',
                        'formatedCustomerEnergy',
                        'formatedEnergy',
                    ]}
                    colStyle={{
                        1: {
                            borderRight: '1px solid var(--gray-500)',
                        },
                        6: {
                            borderRight: '1px solid var(--gray-500)',
                        },
                    }}
                />
                {areaWiseRevenueModal.status && (
                    <ModalComponent
                        isOpen={areaWiseRevenueModal.status}
                        setOpen={() => {
                            setAreaWiseRevenueModal({ status: false, data: null });
                        }}
                    >
                        <div>
                            <AreaEnergyRevenueIndex data={areaWiseRevenueModal.data} setOpen={setAreaWiseRevenueModal} />
                        </div>
                    </ModalComponent>
                )}
                {showConsumptionModal.status && (
                    <ModalComponent
                        isOpen={showConsumptionModal.status}
                        setOpen={() => {
                            setShowConsumptionModal({ status: false, data: null });
                        }}
                    >
                        <div>
                            <ConsumptionIndex data={showConsumptionModal.data} setOpen={setShowConsumptionModal} />
                        </div>
                    </ModalComponent>
                )}
            </div>
        </div>
    );
};

export default Ledger;
